import React from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import styled from 'styled-components';

import AlertError from '@rover/icons/svg/alert-error.svg';
import AlertSuccess from '@rover/icons/svg/alert-success.svg';
import { Box, Display, Flex, Heading, Paragraph } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import PageLayout from '@rover/react-lib/src/components/layouts/PageLayout';
import ResponsiveLayout from '@rover/react-lib/src/components/layouts/ResponsiveLayout';
import inject from '@rover/utilities/injector';

import {
  BACKGROUND_BLUE_100_BRAND_2_COLORS_STYLES,
  GREEN_ICON_BRAND_2_COLORS_STYLES,
  ParagraphBrand2StyledLink,
  RED_ICON_BRAND_2_COLORS_STYLES,
  StyledBox,
  StyledBulletedList,
} from '../brand2ColorStyles.constants';

import Ellipse from './images/ellipse.svg';

const { guaranteeTosUrl, veterinaryCareLimit, emergencyHotlineHours } = inject('Rover.context', {});
const ContentResponsiveLayout = styled(ResponsiveLayout)`
  margin-bottom: ${({ theme }) => theme.space['24x']};
  ${MQ.XS.toString()} {
    margin-bottom: ${({ theme }) => theme.space['16x']};
  }
  ${() => BACKGROUND_BLUE_100_BRAND_2_COLORS_STYLES}
`;
const ResponsivePageLayout = styled(PageLayout)`
  margin-top: ${({ theme }) => theme.space['12x']};
  margin-bottom: ${({ theme }) => theme.space['24x']};
`;

const getListItemsCovers = () => {
  return [
    {
      IconClass: Ellipse,
      title: t({
        message: `Up to ${veterinaryCareLimit} in vet care reimbursement for eligible claims related to injury to either the pet owner's or sitter’s pets.`,
        comment: '{veterinaryCareLimit} is the total amount limit for the veterinary care',
      }),
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Property damage to the pet owner’s home caused by a sitter, or dog walker.`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Certain out-of-pocket medical costs for 3rd party injuries (e.g. someone other than the pet owner, sitter, or dog walker).`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t({
        message: `${emergencyHotlineHours} support.`,
        comment:
          "{emergencyHotlineHours} is '24/7' for en-us, en-ca, fr-ca and empty string for other languages",
      }),
      content: '',
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));
};

const AlertErrorIcon = styled(AlertError)`
  height: ${({ theme }) => theme.space['12x']};
  width: 100%;
  ${() => RED_ICON_BRAND_2_COLORS_STYLES}
  overflow: visible;
  margin-bottom: ${({ theme }) => theme.space['3x']};
  text-align: center;
`;

const AlertSuccessIcon = styled(AlertSuccess)`
  height: ${({ theme }) => theme.space['12x']};
  width: 100%;
  ${() => GREEN_ICON_BRAND_2_COLORS_STYLES}
  margin-bottom: ${({ theme }) => theme.space['3x']};
  overflow: visible;
  text-align: center;
  display-inline: block;
`;

const MoreDetailsSection = (): JSX.Element => {
  const { text, space } = useTheme();

  const listItemsDoesntCover = [
    {
      IconClass: Ellipse,
      title: t`Damages to the sitter’s property or home.`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Injury to sitters and dog walkers.`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Injuries to family members or roommates.`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Vet care costs beyond 30 days.`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Anything pre-existing or preventable — such as fleas and ticks.`,
      content: '',
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));

  return (
    <ContentResponsiveLayout data-testid="more-details-section">
      <ResponsivePageLayout>
        <Display size="500" mb="12x" mt="1x">
          <Trans>Let’s dig into more of the details</Trans>
        </Display>

        <Flex flexWrap="wrap">
          <StyledBox
            variant="card_default"
            flex={['100%', '1']}
            px={['4x', '12x']}
            py="12x"
            background="primary"
            minHeight="505px"
          >
            <AlertSuccessIcon />

            <Heading size="300" textColor="primary" textAlign="center" marginBottom="6x">
              <Trans>The Rover Guarantee covers</Trans>
            </Heading>

            <StyledBulletedList
              fontSize={text['200'].fontSize}
              fontWeight={text['200'].fontWeight}
              height={space['6x']}
              items={getListItemsCovers()}
            />
          </StyledBox>

          <Box
            variant="card_default"
            flex={['100%', '1']}
            px={['4x', '12x']}
            py="12x"
            background="primary"
            minHeight="505px"
          >
            <AlertErrorIcon />

            <Heading size="300" textAlign="center" textColor="primary" marginBottom="6x">
              <Trans>The Rover Guarantee doesn’t cover</Trans>
            </Heading>

            <StyledBulletedList
              fontSize={text['200'].fontSize}
              fontWeight={text['200'].fontWeight}
              height={space['6x']}
              items={listItemsDoesntCover}
            />
          </Box>
        </Flex>

        <Paragraph
          webProps={{ maxWidth: 'none' }}
          size="200"
          textAlign="left"
          marginTop="12x"
          textColor="primary"
        >
          <Trans>
            In order to be eligible, injuries or damages must result from care provided during a
            service booked and paid through the platform. In general, the Guarantee reimburses costs
            in the event the responsible party is unwilling or unable to pay.
          </Trans>
        </Paragraph>
        <a href={guaranteeTosUrl}>
          <ParagraphBrand2StyledLink size="200" mb="0x">
            <Trans> See Rover Guarantee Terms for full details</Trans>
          </ParagraphBrand2StyledLink>
        </a>
      </ResponsivePageLayout>
    </ContentResponsiveLayout>
  );
};

export default MoreDetailsSection;
